import type { ApprovalDto, RunDto, TaskDto } from '@af/shared';
import { isActiveRun, needsPerson } from './run-bulk';

export type ContextKind = 'runs' | 'company' | 'approvals' | 'tasks' | null;

/** Which page-specific block the side panel shows for a route (null = the generic overview only). */
export function contextKindFor(pathname: string): ContextKind {
  const is = (p: string) => pathname === p || pathname.startsWith(`${p}/`);
  if (is('/runs')) return 'runs';
  // The company overview (the chat page with nothing open).
  if (pathname === '/chat') return 'company';
  if (is('/approvals')) return 'approvals';
  if (is('/tasks')) return 'tasks';
  return null;
}

export interface RunsSummary {
  active: number;
  running: number;
  queued: number;
  waitingForYou: number;
  scheduled: number;
  failedToday: number;
  /** Cost of the runs that are still going. */
  activeCostUsd: number;
  /** Active runs, longest running first. */
  longest: RunDto[];
}

export function summarizeRuns(runs: RunDto[], now = Date.now()): RunsSummary {
  const active = runs.filter((r) => isActiveRun(r.status));
  const started = (r: RunDto) => Date.parse(r.startedAt ?? r.createdAt);
  return {
    active: active.length,
    running: active.filter((r) => r.status === 'running').length,
    queued: active.filter((r) => r.status === 'queued').length,
    waitingForYou: active.filter((r) => needsPerson(r.status)).length,
    scheduled: active.filter((r) => r.status === 'scheduled').length,
    failedToday: runs.filter(
      (r) => r.status === 'failed' && now - Date.parse(r.endedAt ?? r.createdAt) < 864e5,
    ).length,
    activeCostUsd: active.reduce((s, r) => s + r.costUsd, 0),
    longest: [...active].sort((a, b) => started(a) - started(b)).slice(0, 3),
  };
}

export interface ApprovalsSummary {
  pending: number;
  byAgent: { agentId: string; name: string; count: number }[];
  byTool: { tool: string; count: number }[];
  /** The pending approval that expires first (null when none). */
  soonest: ApprovalDto | null;
}

export function summarizeApprovals(approvals: ApprovalDto[]): ApprovalsSummary {
  const pending = approvals.filter((a) => a.status === 'pending');
  const count = <K extends string>(key: (a: ApprovalDto) => K) => {
    const m = new Map<K, number>();
    for (const a of pending) m.set(key(a), (m.get(key(a)) ?? 0) + 1);
    return [...m.entries()].sort((x, y) => y[1] - x[1]);
  };
  const names = new Map(pending.map((a) => [a.agentId, a.agentName ?? a.agentId]));
  return {
    pending: pending.length,
    byAgent: count((a) => a.agentId).map(([agentId, n]) => ({
      agentId,
      name: names.get(agentId) ?? agentId,
      count: n,
    })),
    byTool: count((a) => a.toolName).map(([tool, n]) => ({ tool, count: n })),
    soonest: [...pending].sort((a, b) => a.expiresAt.localeCompare(b.expiresAt))[0] ?? null,
  };
}

export interface TasksSummary {
  open: number;
  running: number;
  queued: number;
  waiting: number;
  overdue: TaskDto[];
  failedRecently: number;
  oldestQueued: TaskDto | null;
}

const DONE = new Set(['done', 'failed', 'cancelled']);

export function summarizeTasks(tasks: TaskDto[], now = Date.now()): TasksSummary {
  const open = tasks.filter((t) => !DONE.has(t.status));
  return {
    open: open.length,
    running: open.filter((t) => t.status === 'running' || t.status === 'waiting_children').length,
    queued: open.filter((t) => t.status === 'queued' || t.status === 'todo').length,
    waiting: open.filter((t) => t.status === 'awaiting_approval').length,
    overdue: open.filter((t) => t.dueAt && Date.parse(t.dueAt) < now),
    failedRecently: tasks.filter(
      (t) => t.status === 'failed' && now - Date.parse(t.completedAt ?? t.createdAt) < 864e5,
    ).length,
    oldestQueued:
      [...open]
        .filter((t) => t.status === 'queued' || t.status === 'todo')
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0] ?? null,
  };
}
